/**
 * ChannelCard — Card for a single channel integration showing status and actions.
 */
import { Mail, MessageSquare, Phone, Send, Hash, Wifi } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import type { ChannelConfig } from "@ajnt/shared";

const channelIcons: Record<string, React.ReactNode> = {
  slack: <Hash className="h-6 w-6" />,
  email: <Mail className="h-6 w-6" />,
  sms: <Phone className="h-6 w-6" />,
  telegram: <Send className="h-6 w-6" />,
  whatsapp: <MessageSquare className="h-6 w-6" />,
};

const channelColors: Record<string, string> = {
  slack: "bg-purple-500/10 text-purple-400",
  email: "bg-blue-500/10 text-blue-400",
  sms: "bg-green-500/10 text-green-400",
  telegram: "bg-sky-500/10 text-sky-400",
  whatsapp: "bg-emerald-500/10 text-emerald-400",
};

interface ChannelCardProps {
  config: ChannelConfig;
  onTest: () => void;
  onEdit: () => void;
  onRemove: () => void;
}

export function ChannelCard({ config, onTest, onEdit, onRemove }: ChannelCardProps) {
  const icon = channelIcons[config.channelType] ?? <Wifi className="h-6 w-6" />;
  const colorClass = channelColors[config.channelType] ?? "bg-white/10 text-white";

  return (
    <div
      className="rounded-xl border border-white/10 bg-surface-raised p-5 transition-colors hover:border-white/20"
      data-testid={`channel-card-${config.id}`}
    >
      {/* Icon + name */}
      <div className="flex items-start justify-between">
        <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${colorClass}`}>
          {icon}
        </div>
        <div className="flex items-center gap-1.5">
          <div
            className={`h-2.5 w-2.5 rounded-full ${
              config.active ? "bg-emerald-400" : "bg-red-400"
            }`}
            data-testid="channel-status-dot"
          />
          <span className="text-xs text-muted">
            {config.active ? "Connected" : "Disconnected"}
          </span>
        </div>
      </div>

      <h3 className="mt-3 text-base font-semibold capitalize text-white">
        {config.channelType}
      </h3>
      <Badge variant="default" className="mt-1 text-[10px]">
        Priority {config.priority}
      </Badge>

      {config.lastInteractionAt && (
        <p className="mt-2 text-xs text-muted-foreground">
          Last active: {new Date(config.lastInteractionAt).toLocaleDateString()}
        </p>
      )}

      {/* Actions */}
      <div className="mt-4 flex gap-2">
        <Button variant="secondary" size="sm" onClick={onTest}>
          Test
        </Button>
        <Button variant="ghost" size="sm" onClick={onEdit}>
          Edit
        </Button>
        <Button variant="ghost" size="sm" onClick={onRemove} className="text-danger hover:text-danger">
          Remove
        </Button>
      </div>
    </div>
  );
}
